<template>
  <div class="tracked-artefacts">
    <div class="header">
      <h2>Отслеживаемые артефакты</h2>
      <Button
        label="Добавить артефакт"
        icon="pi pi-plus"
        @click="showAddDialog = true"
        class="p-button-success"
      />
    </div>

    <DataTable
      :value="trackedArtefacts"
      :loading="loading"
      stripedRows
      responsiveLayout="scroll"
      :paginator="true"
      :rows="10"
      :rowsPerPageOptions="[10, 25, 50]"
      currentPageReportTemplate="Показано {first} - {last} из {totalRecords} артефактов"
    >
      <Column field="artefactId" header="Артефакт">
        <template #body="{ data }">
          {{ getArtefactName(data.artefactId) }}
        </template>
      </Column>
      <Column field="quality" header="Качество">
        <template #body="{ data }">
          <Tag
            :value="getQualityLabel(data.quality)"
            :style="{
              backgroundColor: getQualityColor(data.quality),
              color: getQualityTextColor(data.quality),
              border: `2px solid ${getQualityBorderColor(data.quality)}`
            }"
          />
        </template>
      </Column>
      <Column field="sharpening" header="Заточка">
        <template #body="{ data }">
          <Tag :value="getSharpeningLabel(data.sharpening)" severity="info" />
        </template>
      </Column>
      <Column field="buyBelowPrice" header="Купить ниже">
        <template #body="{ data }">
          <span class="price" v-if="data.buyBelowPrice">{{ formatPrice(data.buyBelowPrice) }}</span>
          <span class="price" v-else>—</span>
        </template>
      </Column>
      <Column field="sellAbovePrice" header="Продать выше">
        <template #body="{ data }">
          <span class="price" v-if="data.sellAbovePrice">{{
            formatPrice(data.sellAbovePrice)
          }}</span>
          <span class="price" v-else>—</span>
        </template>
      </Column>
      <Column field="createdAt" header="Создан">
        <template #body="{ data }">
          {{ formatDate(data.createdAt) }}
        </template>
      </Column>
      <Column header="Действия">
        <template #body="{ data }">
          <div class="actions">
            <Button
              icon="pi pi-pencil"
              class="p-button-rounded p-button-text p-button-info"
              @click="editArtefact(data)"
              v-tooltip="'Редактировать'"
            />
            <Button
              icon="pi pi-trash"
              class="p-button-rounded p-button-text p-button-danger"
              @click="confirmDelete(data)"
              v-tooltip="'Удалить'"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Диалог добавления/редактирования -->
    <Dialog
      v-model:visible="showAddDialog"
      :header="editingArtefact ? 'Редактировать артефакт' : 'Добавить артефакт'"
      :modal="true"
      :style="{ width: '700px' }"
    >
      <form @submit.prevent="saveArtefact">
        <div class="form-group">
          <label for="artefactId">Артефакт</label>
          <Dropdown
            id="artefactId"
            v-model="form.artefactId"
            :options="artefactOptions"
            optionLabel="name"
            optionValue="id"
            placeholder="Выберите артефакт"
            required
            filter
          />
        </div>

        <div class="form-group">
          <label for="quality">Качество</label>
          <div class="quality-buttons">
            <Button
              v-for="quality in QUALITY_LIST"
              :key="quality.value"
              :label="quality.label"
              :style="{
                backgroundColor: quality.bgColor,
                borderColor: quality.borderColor,
                color: quality.color,
                border: '2px solid'
              }"
              class="quality-btn"
              :class="{ active: form.quality === quality.value }"
              @click="form.quality = quality.value"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="sharpening">Заточка</label>
          <div class="sharpening-buttons">
            <Button
              v-for="sharpening in SHARPENING_LIST"
              :key="sharpening.value"
              :label="sharpening.label"
              :style="{
                backgroundColor: sharpening.color,
                borderColor: sharpening.color,
                color: 'white',
                border: '2px solid'
              }"
              class="sharpening-btn"
              :class="{ active: form.sharpening === sharpening.label }"
              @click="form.sharpening = sharpening.label"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="buyBelowPrice">Цена покупки (ниже)</label>
          <InputNumber id="buyBelowPrice" v-model="form.buyBelowPrice" :min="0" />
        </div>

        <div class="form-group">
          <label for="sellAbovePrice">Цена продажи (выше)</label>
          <InputNumber id="sellAbovePrice" v-model="form.sellAbovePrice" :min="0" />
        </div>

        <div class="form-actions">
          <Button label="Отмена" icon="pi pi-times" class="p-button-text" @click="cancelForm" />
          <Button
            :label="editingArtefact ? 'Сохранить' : 'Добавить'"
            icon="pi pi-check"
            type="submit"
            :loading="saving"
          />
        </div>
      </form>
    </Dialog>

    <!-- Диалог подтверждения удаления -->
    <Dialog
      v-model:visible="showDeleteDialog"
      header="Подтверждение удаления"
      :modal="true"
      :style="{ width: '400px' }"
    >
      <p>Вы уверены, что хотите удалить этот артефакт из отслеживаемых?</p>
      <template #footer>
        <Button
          label="Отмена"
          icon="pi pi-times"
          class="p-button-text"
          @click="showDeleteDialog = false"
        />
        <Button
          label="Удалить"
          icon="pi pi-trash"
          class="p-button-danger"
          @click="deleteArtefact"
          :loading="deleting"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import { api } from '@/api'
import { listArt } from '@/common/list'
import { getNameArtFromId } from '@/common/list'
import { QUALITY_LIST, SHARPENING_LIST } from '@/common/list'

const toast = useToast()

const trackedArtefacts = ref([])
const loading = ref(false)
const saving = ref(false)
const deleting = ref(false)

const showAddDialog = ref(false)
const showDeleteDialog = ref(false)
const editingArtefact = ref(null)
const deletingArtefact = ref(null)

// Опции для выпадающего списка артефактов
const artefactOptions = ref(listArt)

const form = ref({
  artefactId: '',
  quality: 0,
  sharpening: '0-4',
  buyBelowPrice: null,
  sellAbovePrice: null
})

const loadTrackedArtefacts = async () => {
  loading.value = true
  try {
    const response = await api.get('/tracked-artefacts')
    trackedArtefacts.value = response.data
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить отслеживаемые артефакты',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

const saveArtefact = async () => {
  saving.value = true
  try {
    console.log('Form value before sending:', form.value)

    if (editingArtefact.value) {
      console.log('Sending PATCH request...')
      const response = await api.patch(`/tracked-artefacts/${editingArtefact.value.id}`, form.value)
      console.log('PATCH response:', response)
      toast.add({
        severity: 'success',
        summary: 'Успех',
        detail: 'Артефакт обновлен',
        life: 3000
      })
    } else {
      console.log('Sending POST request...')
      const response = await api.post('/tracked-artefacts', form.value)
      console.log('POST response:', response)
      toast.add({
        severity: 'success',
        summary: 'Успех',
        detail: 'Артефакт добавлен',
        life: 3000
      })
    }

    cancelForm()
    await loadTrackedArtefacts()
  } catch (error) {
    console.error('Error saving artefact:', error)
    console.error('Error response:', error.response)
    console.error('Error data:', error.response?.data)
    console.error('Error status:', error.response?.status)

    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.message || 'Не удалось сохранить артефакт',
      life: 3000
    })
  } finally {
    saving.value = false
  }
}

const editArtefact = artefact => {
  editingArtefact.value = artefact
  form.value = {
    artefactId: artefact.artefactId,
    quality: artefact.quality,
    sharpening: getSharpeningLabel(artefact.sharpening),
    buyBelowPrice: artefact.buyBelowPrice,
    sellAbovePrice: artefact.sellAbovePrice
  }
  showAddDialog.value = true
}

const confirmDelete = artefact => {
  deletingArtefact.value = artefact
  showDeleteDialog.value = true
}

const deleteArtefact = async () => {
  deleting.value = true
  try {
    await api.delete(`/tracked-artefacts/${deletingArtefact.value.id}`)
    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: 'Артефакт удален',
      life: 3000
    })
    showDeleteDialog.value = false
    deletingArtefact.value = null
    await loadTrackedArtefacts()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось удалить артефакт',
      life: 3000
    })
  } finally {
    deleting.value = false
  }
}

const cancelForm = () => {
  showAddDialog.value = false
  editingArtefact.value = null
  form.value = {
    artefactId: '',
    quality: 0,
    sharpening: '0-4',
    buyBelowPrice: null,
    sellAbovePrice: null
  }
}

const getQualitySeverity = quality => {
  if (quality >= 8) return 'success'
  if (quality >= 5) return 'info'
  if (quality >= 3) return 'warning'
  return 'danger'
}

const formatPrice = price => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0
  }).format(price)
}

const formatDate = dateString => {
  return new Date(dateString).toLocaleString('ru-RU')
}

const getArtefactName = artefactId => {
  return getNameArtFromId(artefactId)
}

const getQualityColor = quality => {
  const qualityItem = QUALITY_LIST.find(q => q.value === quality)
  return qualityItem ? qualityItem.bgColor : '#6c757d'
}

const getQualityTextColor = quality => {
  const qualityItem = QUALITY_LIST.find(q => q.value === quality)
  return qualityItem ? qualityItem.color : '#ffffff'
}

const getQualityBorderColor = quality => {
  const qualityItem = QUALITY_LIST.find(q => q.value === quality)
  return qualityItem ? qualityItem.borderColor : '#6c757d'
}

const getQualityLabel = quality => {
  const qualityItem = QUALITY_LIST.find(q => q.value === quality)
  return qualityItem ? qualityItem.label : `${quality}`
}

const getSharpeningLabel = sharpening => {
  const sharpeningItem = SHARPENING_LIST.find(s => s.value === sharpening)
  return sharpeningItem ? sharpeningItem.label : `${sharpening}`
}

onMounted(() => {
  loadTrackedArtefacts()
})
</script>

<style scoped>
.tracked-artefacts {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.price {
  font-weight: 600;
  color: #2196f3;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 2rem;
}

.quality-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.quality-btn {
  min-width: 80px;
  height: 45px;
  padding: 0 1rem;
  border: 2px solid;
  color: white !important;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-radius: 6px;
}

.quality-btn:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

.quality-btn.active {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.5);
  transform: scale(1.1);
}

.sharpening-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.sharpening-btn {
  min-width: 80px;
  height: 45px;
  padding: 0 1rem;
  border: 2px solid;
  color: white !important;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-radius: 6px;
}

.sharpening-btn:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

.sharpening-btn.active {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.5);
  transform: scale(1.1);
}
</style>
